import { memo } from 'react';

import { FlameStage } from './model/milestones';

import styles from './Medallions.module.scss';

/**
 * The streak flame medallion (canvas: Flame). Colour follows the streak:
 * red before the first status, then orange, gold, white, white-blue, blue.
 * Stage 0 is the dashed outline (no streak), `ember` the coal after an end.
 */
export type FlameProps = {
  stage: FlameStage | 'ember';
  size: number;
  /** Shrunk at last call. */
  low?: boolean;
  dim?: boolean;
  /** Orange ring on the next ladder rung. */
  ring?: boolean;
  /** An unreached ladder rung: a recessed opaque socket on the cream card. */
  socket?: boolean;
  /** Play the one-shot flare (a day just counted). */
  flare?: boolean;
};

type Palette = {
  o: [string, string, string];
  m: [string, string];
  c: [string, string];
  glow: string;
  rim: string;
};

const PALETTES: Record<1 | 2 | 3 | 4 | 5 | 6, Palette> = {
  1: {
    o: ['#ffb066', '#e0521f', '#7f1f0c'],
    m: ['#ffc58a', '#ec6a24'],
    c: ['#ffe6bd', '#ffb45c'],
    glow: 'rgba(235,95,35,0.55)',
    rim: '#7a4a24'
  },
  2: {
    o: ['#ffd089', '#e8821f', '#9c2f12'],
    m: ['#ffe0a8', '#f39a34'],
    c: ['#fff6df', '#ffd089'],
    glow: 'rgba(240,140,40,0.6)',
    rim: '#b0753d'
  },
  3: {
    o: ['#ffe58f', '#f0a81c', '#a3540d'],
    m: ['#fff0b8', '#ffc43f'],
    c: ['#fffdf0', '#ffeaa6'],
    glow: 'rgba(255,192,64,0.6)',
    rim: '#c9ced6'
  },
  4: {
    o: ['#fffaf0', '#f6d06a', '#b5761a'],
    m: ['#ffffff', '#ffe9a6'],
    c: ['#ffffff', '#fffbea'],
    glow: 'rgba(255,226,150,0.68)',
    rim: '#e3b84f'
  },
  5: {
    o: ['#ffffff', '#ffe7b0', '#4f86dc'],
    m: ['#ffffff', '#d6e9ff'],
    c: ['#ffffff', '#f4faff'],
    glow: 'rgba(165,208,255,0.62)',
    rim: '#e8eef6'
  },
  6: {
    o: ['#d8f0ff', '#4c8bff', '#2230a6'],
    m: ['#effaff', '#93c7ff'],
    c: ['#ffffff', '#e9f7ff'],
    glow: 'rgba(92,150,255,0.75)',
    rim: '#a9e4ff'
  }
};

export const OUTER =
  'M24 3C26.2 10.5 35.5 15.5 35.5 27.5C35.5 37.2 30.4 44.5 24 44.5C17.6 44.5 12.5 37.6 12.5 29.2C12.5 23.2 15.4 19 18.4 16C18.2 20.6 19.8 23.6 22.2 25C21 17.6 21.6 9.8 24 3Z';
const MID =
  'M24 15.5C25.8 21 31.4 24.6 31.4 32C31.4 38.6 28.1 42.6 24 42.6C19.9 42.6 16.6 38.9 16.6 33.6C16.6 29.6 18.9 27 20.7 25.2C20.8 28.6 21.9 30.5 23.4 31.5C22.6 26 22.9 20.4 24 15.5Z';
export const CORE =
  'M24 26.5C25.1 30 28.6 31.9 28.6 36C28.6 39.4 26.6 41.6 24 41.6C21.4 41.6 19.4 39.4 19.4 36.4C19.4 33.6 21.1 32.1 22.5 30.8C22.7 32.6 23.2 33.6 24 34.1C23.5 31.6 23.4 29.1 24 26.5Z';

function discShadow(rim: string, ring: boolean): string {
  let s = `inset 0 1px 0 rgba(255,232,196,0.24), inset 0 -4px 8px rgba(0,0,0,0.55), 0 0 0 1.5px ${rim}, 0 0 0 3px rgba(18,9,3,0.5)`;
  if (ring) s += ', 0 0 0 5px #e8821f, 0 0 0 9px rgba(232,130,31,0.24)';
  return `${s}, 0 3px 8px rgba(0,0,0,0.45)`;
}

function Flame({ stage, size, low, dim, ring, socket, flare }: FlameProps) {
  const lit = stage !== 0 && stage !== 'ember';
  const palette = lit ? PALETTES[stage as 1 | 2 | 3 | 4 | 5 | 6] : null;
  const isSocket = !!socket && stage === 0;
  const rim = palette ? palette.rim : stage === 'ember' ? '#6b3a18' : '#5a3d24';
  // Gradient ids only need to be unique per palette: identical definitions
  // sharing an id render the same.
  const id = `sf${stage}`;
  const glow = palette ? palette.glow : stage === 'ember' ? 'rgba(232,110,30,0.42)' : null;
  const glowInset = -Math.round(size * 0.34);

  return (
    <div
      className={`${styles.medallion} ${dim ? styles.dim : ''}`}
      style={{ width: size, height: size }}
      aria-hidden='true'>
      {glow && !isSocket && (
        <div
          className={styles.halo}
          style={{
            inset: glowInset,
            background: `radial-gradient(closest-side, ${glow}, rgba(0,0,0,0))`
          }}
        />
      )}
      <div
        className={styles.disc}
        style={{
          background: isSocket
            ? 'radial-gradient(circle at 50% 35%, #e2d3b0 0%, #d6c49c 100%)'
            : 'radial-gradient(circle at 50% 28%, #402816 0%, #1e120a 58%, #120a04 100%)',
          boxShadow: isSocket
            ? 'inset 0 2px 3px rgba(43,23,10,0.32), inset 0 -1px 0 rgba(255,255,255,0.6), 0 0 0 1px rgba(74,35,10,0.22)'
            : discShadow(rim, !!ring)
        }}>
        <div
          className={`${styles.flameBody} ${low ? styles.low : ''} ${
            flare && lit ? styles.flare : ''
          }`}>
          <svg className={styles.svg} viewBox='0 0 48 48'>
            {palette && (
              <>
                <defs>
                  <radialGradient id={`${id}o`} cx='50%' cy='82%' r='74%'>
                    <stop offset='0%' stopColor={palette.o[0]} />
                    <stop offset='50%' stopColor={palette.o[1]} />
                    <stop offset='100%' stopColor={palette.o[2]} />
                  </radialGradient>
                  <radialGradient id={`${id}m`} cx='50%' cy='86%' r='70%'>
                    <stop offset='0%' stopColor={palette.m[0]} />
                    <stop offset='100%' stopColor={palette.m[1]} />
                  </radialGradient>
                  <radialGradient id={`${id}c`} cx='50%' cy='88%' r='66%'>
                    <stop offset='0%' stopColor={palette.c[0]} />
                    <stop offset='100%' stopColor={palette.c[1]} />
                  </radialGradient>
                </defs>
                <path d={OUTER} fill={`url(#${id}o)`} />
                <path d={MID} fill={`url(#${id}m)`} />
                <path d={CORE} fill={`url(#${id}c)`} />
              </>
            )}
            {stage === 0 && (
              <path
                d={OUTER}
                fill={isSocket ? 'rgba(74,35,10,0.05)' : 'rgba(241,227,199,0.05)'}
                stroke={isSocket ? 'rgba(74,35,10,0.42)' : 'rgba(241,227,199,0.55)'}
                strokeWidth='1.5'
                strokeDasharray='3 2.6'
                strokeLinejoin='round'
              />
            )}
            {stage === 'ember' && (
              <>
                <defs>
                  <radialGradient id='sfEmberCoal' cx='50%' cy='50%' r='50%'>
                    <stop offset='0%' stopColor='#ffc06a' />
                    <stop offset='45%' stopColor='#e8621f' />
                    <stop offset='100%' stopColor='rgba(122,58,16,0)' />
                  </radialGradient>
                </defs>
                <path
                  d={OUTER}
                  fill='rgba(122,58,16,0.2)'
                  stroke='rgba(232,130,31,0.45)'
                  strokeWidth='1.3'
                  strokeLinejoin='round'
                />
                <ellipse cx='24' cy='39.5' rx='8' ry='4.2' fill='url(#sfEmberCoal)' />
              </>
            )}
          </svg>
        </div>
        {!isSocket && (
          <div
            className={styles.glass}
            style={{
              background:
                'linear-gradient(180deg, rgba(255,238,210,0.16) 0%, rgba(255,238,210,0) 100%)'
            }}
          />
        )}
      </div>
    </div>
  );
}

export default memo(Flame);
